import { useEffect, useState } from "react"
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Card,
  CardContent,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  TextField,
  Divider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import {
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Payment as PaymentIcon,
  Home as HomeIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"

const ClientsDash = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const baseUrl = process.env.REACT_APP_APP_URL
  const [seances, setSeances] = useState([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedUserSeances, setSelectedUserSeances] = useState(null)

  const token = localStorage.getItem("CC_Token")
  const user = JSON.parse(localStorage.getItem("user"))
  const userId = user ? user.id : null

  useEffect(() => {
    if (!token || !userId) {
      console.error("Token or User ID is missing.")
      return
    }

    const fetchSeances = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/seances`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        
        setSeances(response.data)
      } catch (error) {
        console.error("Error fetching seances:", error)
        toast.error("Erreur lors de la récupération des séances")
      }
    }

    fetchSeances()
  }, [token, userId, baseUrl])

  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    localStorage.removeItem("user")
    toast.success("Vous avez été déconnecté avec succès.", {
      position: "top-right",
      autoClose: 3000,
    })
    setTimeout(() => navigate("/login"), 4000)
  }

  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs")
  }

  const handleProfile = () => {
    user.role === "admin" ? navigate("/ProfileAdmin") : navigate("/Profile")
  }

  // Group seances by user
  const groupedSeances = seances.reduce((acc, seance) => {
    const userId = seance.user_id
    if (!acc[userId]) {
      acc[userId] = {
        user: seance.user,
        seances: [],
      }
    }
    acc[userId].seances.push(seance)
    return acc
  }, {})

  // Filter based on search term
  const filteredGroups = Object.values(groupedSeances).filter(
    (group) =>
      searchTerm === "" ||
      group.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.seances.some((seance) => seance.time.toLowerCase().includes(searchTerm.toLowerCase())),
  )

  // Handle delete confirmation dialog
  const handleOpenDeleteDialog = (userSeances) => {
    setSelectedUserSeances(userSeances)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setSelectedUserSeances(null)
  }

  // Handle delete seances
  const handleDeleteSeances = async () => {
    if (!selectedUserSeances) return

    try {
      // Delete all seances for the selected user
      await Promise.all(
        selectedUserSeances.seances.map((seance) =>
          axios.delete(`${baseUrl}/api/seances/${seance.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ),
      )

      // Update the local state by removing the deleted seances
      setSeances((prevSeances) =>
        prevSeances.filter((seance) => !selectedUserSeances.seances.find((s) => s.id === seance.id)),
      )

      toast.success(`Les séances de ${selectedUserSeances.user.name} ont été supprimées avec succès.`)
    } catch (error) {
      console.error("Error deleting seances:", error)
      toast.error("Erreur lors de la suppression des séances")
    }

    handleCloseDeleteDialog()
  }

  return (
    <div>
      <ToastContainer />
      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar alt="Logo" src={"../assets/cej.png"} sx={{ width: 40, height: 40, marginRight: "0.5rem" }} />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ marginTop: "64px", padding: "16px", paddingBottom: "80px" }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          label="Rechercher par cavalier ou date"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ marginBottom: "16px" }}
        />

        {/* Seances grouped by user */}
        {filteredGroups.map((group) => (
          <Box key={group.user.id} sx={{ mb: 4 }}>
            {/* User section title */}
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Cavalier: {group.user.name}
                
                
              </Typography>
            </Box>

            {/* Single card containing all seances for this user */}
            <Box sx={{ maxWidth: 600, mx: "auto" }}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Nom du Coach : {group.seances.length > 0 && group.seances[0].coach ? group.seances[0].coach.name : "Chargement..."}
                  </Typography>
                  {group.seances.map((seance) => (
                    <Box key={seance.id} sx={{ mb: 1 }}>
                      <Typography variant="body2">Date: {seance.time}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleOpenDeleteDialog(group)}
                sx={{
                  mb: 2,
                  height: "48px",
                  fontSize: "1rem",
                  backgroundColor: "#d32f2f",
                  "&:hover": {
                    backgroundColor: "#b71c1c",
                  },
                }}
              >
                Supprimer toutes les séances
              </Button>
            </Box>
            <Divider sx={{ mt: 4 }} />
          </Box>
        ))}

        {filteredGroups.length === 0 && (
          <Typography variant="body1" textAlign="center" color="text.secondary">
            Aucune séance trouvée
          </Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "500px",
            p: 2,
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer toutes les séances de {selectedUserSeances?.user.name} ? Cette action est
            irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteSeances} color="error" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{
            backgroundColor: "#003366",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminChrono")}
          />
          <BottomNavigationAction
            label="Home"
            icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate(user.role === "admin" ? "/admin" : "/InstructorHome")}
          />
          <BottomNavigationAction
            label="Paiement"
            icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate(user.role === "admin" ? "/AdminPaiment" : "/InstructorHome")}
          />
        </BottomNavigation>
      </Box>
    </div>
  )
}

export default ClientsDash

